import * as Sentry from '@sentry/react';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listRetailerRegionsV1RetailerRegionsGetOptions,
  queryUtils,
  useAddRetailerRegionToOrganizationMutation,
  useAddUserToOrganizationMutation,
  useCreateOrganizationIcpProductCategoryMutation,
  useCreateOrganizationMutation,
} from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { FEATURE_FLAGS, RetailerKey, SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import type { OrganizationFormSchema } from '@vizit/organisms';
import * as React from 'react';
import { useGetICPProductCategories } from '../../hooks/useGetIcpProductCategories';
import {
  addUserToOrganization,
  associateIcpProductCategories,
  associateRetailerRegions,
  createOrganization,
} from '../../utils';

interface UseCreateOrganizationDialogProps {
  /**
   * The user id
   */
  userId?: string | null;
  /**
   * The user email
   */
  userEmail?: string | null;
}

async function updateQueryCache(queryClient: QueryClient, userId: string) {
  Sentry.addBreadcrumb({
    message: 'Invalidating organizations query for user.',
    level: 'info',
    data: { user_id: userId },
  });

  queryUtils.invalidateOrganizationsForUser(queryClient, userId);
}

function useCreateOrganizationDialog(props: UseCreateOrganizationDialogProps) {
  const { userId, userEmail } = props;

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { icpProductCategoriesMap: icpProductCategories } = useGetICPProductCategories();

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const { data: retailersData } = useQuery({
    ...listRetailerRegionsV1RetailerRegionsGetOptions(),
    enabled: isRetailerEnabled,
  });

  const retailerRegions = React.useMemo(() => {
    if (!isRetailerEnabled || !retailersData) return undefined;
    const allowed: string[] = [RetailerKey.AmazonUS, RetailerKey.WalmartUS];
    return retailersData
      .filter((retailer) => allowed.includes(retailer.id))
      .reduce(
        (acc, retailer) => {
          acc[retailer.id] = retailer.display_name;
          return acc;
        },
        {} as Record<string, string>,
      );
  }, [isRetailerEnabled, retailersData]);

  const { mutateAsync: createOrganizationMutation } = useCreateOrganizationMutation();
  const { mutateAsync: createOrganizationIcpProductCategoryMutation } =
    useCreateOrganizationIcpProductCategoryMutation();
  const { mutateAsync: addUserToOrganizationMutation } = useAddUserToOrganizationMutation();
  const { mutateAsync: addRetailerRegionToOrganizationMutation } = useAddRetailerRegionToOrganizationMutation();

  const handleOnCreateOrganization = React.useCallback(
    async (data: OrganizationFormSchema) => {
      Sentry.withScope((scope) => {
        scope.setUser({ id: userId || undefined, user_email: userEmail });

        Sentry.addBreadcrumb({
          message: 'Starting organization creation flow.',
          level: 'info',
          data: { user_id: userId, organization_name: data.organizationName },
        });
      });

      try {
        const response = await createOrganization(data, addToast, createOrganizationMutation);

        if (response?.id) {
          await associateIcpProductCategories(
            response.id,
            data,
            addToast,
            createOrganizationIcpProductCategoryMutation,
          );

          // Amazon US is added by the backend automatically — exclude it from the mutation.
          // The Walmart US default stays in the form while the retailer list is hidden, so link only listed ones.
          if (isRetailerEnabled) {
            const retailersToAssociate = data.retailers.filter(
              (id) => id !== RetailerKey.AmazonUS && retailerRegions?.[id] !== undefined,
            );
            if (retailersToAssociate.length > 0) {
              await associateRetailerRegions(
                response.id,
                retailersToAssociate,
                addToast,
                addRetailerRegionToOrganizationMutation,
              );
            }
          }

          await addUserToOrganization(response.id, addToast, addUserToOrganizationMutation, userEmail);

          if (userId) {
            await updateQueryCache(queryClient, userId);
          }

          Sentry.addBreadcrumb({
            message: 'Organization creation flow completed',
            level: 'info',
            data: { organization_id: response.id },
          });
        } else {
          throw new Error('Failed to create organization');
        }
      } catch (error) {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Organization,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Organization].createOrganization,
          },
          extra: {
            organization_name: data.organizationName,
            account_type: data.accountType,
            l1_product_categories: data.categories,
          },
        });

        addToast({
          title: 'Failed to create organization',
          description: 'Please try again in a few minutes.',
          level: AlertLevel.Error,
        });
      }
    },
    [
      addToast,
      createOrganizationMutation,
      createOrganizationIcpProductCategoryMutation,
      addRetailerRegionToOrganizationMutation,
      addUserToOrganizationMutation,
      isRetailerEnabled,
      retailerRegions,
      userEmail,
      userId,
      queryClient,
    ],
  );

  return { icpProductCategories, retailerRegions, onCreateOrganization: handleOnCreateOrganization };
}

export { useCreateOrganizationDialog };
export type { UseCreateOrganizationDialogProps };
